import type { ButtonSize } from "@design-system/Button/Button";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { buttonVariants, ghostLabelStyles } from "./ButtonPlayground.lib";
import type { ButtonVariant, GhostLabelStyle } from "./ButtonPlayground.lib";
import { BooleanControl, PlaygroundSelect } from "./playgroundControls";
import styles from "./DatePickerPlayground.module.scss";

/**
 * @purpose The Button sandbox's props aside. Split out of `ButtonPlayground` so
 * the playground reads as the preview it configures. These same controls drive
 * the x-ray, so nothing here changes when the playground flips into it.
 */

export interface ButtonFeatureFlags {
    isDisabled: boolean;
    hasLeftIcon: boolean;
    hasRightIcon: boolean;
    hasSlotBox: boolean;
    isIconOnly: boolean;
    isBlock: boolean;
    hasTooltip: boolean;
}

export const ButtonPlaygroundControls = ({
    variant,
    onVariantChange,
    size,
    sizeOptions,
    onSizeChange,
    labelStyle,
    onLabelStyleChange,
    features,
    onFeaturesChange,
}: {
    variant: ButtonVariant;
    onVariantChange: (variant: ButtonVariant) => void;
    size: ButtonSize;
    sizeOptions: readonly ButtonSize[];
    onSizeChange: (size: ButtonSize) => void;
    labelStyle: GhostLabelStyle;
    onLabelStyleChange: (labelStyle: GhostLabelStyle) => void;
    features: ButtonFeatureFlags;
    onFeaturesChange: (features: ButtonFeatureFlags) => void;
}) => (
    <>
        <section>
            <h2>Variant</h2>
            <PlaygroundSelect<ButtonVariant>
                options={buttonVariants}
                value={variant}
                onChange={onVariantChange}
                searchPlaceholder="Search variants..."
            />
            <h2 className={styles.subheading}>Size</h2>
            <ToggleGroup.Root
                value={size}
                onValueChange={(value) => {
                    if (value) {
                        onSizeChange(value);
                    }
                }}
            >
                {sizeOptions.map((option) => (
                    <ToggleGroup.Item key={option} value={option}>
                        {option}
                    </ToggleGroup.Item>
                ))}
            </ToggleGroup.Root>
            {variant === "ghost" ? (
                <>
                    <h2 className={styles.subheading}>Label style</h2>
                    <ToggleGroup.Root
                        value={labelStyle}
                        onValueChange={(value) => {
                            if (value) {
                                onLabelStyleChange(value);
                            }
                        }}
                    >
                        {ghostLabelStyles.map((option) => (
                            <ToggleGroup.Item key={option} value={option}>
                                {option}
                            </ToggleGroup.Item>
                        ))}
                    </ToggleGroup.Root>
                </>
            ) : null}
        </section>
        <section>
            <h2>Features</h2>
            <div className={styles.booleanGrid}>
                <BooleanControl
                    label="Disabled"
                    checked={features.isDisabled}
                    onChange={(isDisabled) =>
                        onFeaturesChange({ ...features, isDisabled })
                    }
                />
                <BooleanControl
                    label="Left icon"
                    checked={features.hasLeftIcon}
                    disabled={features.isIconOnly}
                    onChange={(hasLeftIcon) =>
                        onFeaturesChange({ ...features, hasLeftIcon })
                    }
                />
                <BooleanControl
                    label="Right icon"
                    checked={features.hasRightIcon}
                    disabled={features.isIconOnly}
                    onChange={(hasRightIcon) =>
                        onFeaturesChange({ ...features, hasRightIcon })
                    }
                />
                <BooleanControl
                    label="Slot chips"
                    checked={features.hasSlotBox}
                    disabled={features.isIconOnly}
                    onChange={(hasSlotBox) =>
                        onFeaturesChange({ ...features, hasSlotBox })
                    }
                />
                <BooleanControl
                    label="Icon only"
                    checked={features.isIconOnly}
                    onChange={(isIconOnly) =>
                        onFeaturesChange({ ...features, isIconOnly })
                    }
                />
                <BooleanControl
                    label="Block"
                    checked={features.isBlock}
                    onChange={(isBlock) =>
                        onFeaturesChange({ ...features, isBlock })
                    }
                />
                <BooleanControl
                    label="Tooltip"
                    checked={features.hasTooltip}
                    onChange={(hasTooltip) =>
                        onFeaturesChange({ ...features, hasTooltip })
                    }
                />
            </div>
        </section>
    </>
);
